import { useEffect, useState, useCallback } from "react";
import { Search, Trash2, Power } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, Spinner } from "@/components/shared";

const ROLES = ["masquerader", "band_leader", "brand_partner", "admin"];
const ROLE_LABEL = { masquerader: "Masquerader", band_leader: "Band Leader", brand_partner: "Brand Partner", admin: "Admin" };

export default function AdminParticipants() {
  const [users, setUsers] = useState(null);
  const [bands, setBands] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (roleFilter) params.role = roleFilter;
    if (q) params.q = q;
    api.get("/admin/users", { params }).then(({ data }) => setUsers(data));
  }, [roleFilter, q]);

  useEffect(() => { api.get("/admin/bands").then(({ data }) => setBands(data)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const setRole = async (u, role) => {
    await api.put(`/admin/users/${u.id}`, { role });
    toast.success(`${u.name} is now ${ROLE_LABEL[role]}`);
    load();
  };
  const setBand = async (u, band_id) => {
    await api.put(`/admin/users/${u.id}`, { band_id });
    toast.success("Band reassigned");
    load();
  };
  const toggleActive = async (u) => {
    await api.put(`/admin/users/${u.id}`, { active: !u.active });
    toast.success(u.active ? "User suspended" : "User reactivated");
    load();
  };
  const del = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <Page>
      <h1 className="font-head font-extrabold text-3xl">Participants</h1>
      <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>Manage every user across the platform</p>

      <div className="flex flex-wrap gap-2 mt-5">
        <div className="flex items-center gap-2 rounded-xl px-3 flex-1 min-w-[200px]" style={{ background: "var(--cs-card)", border: "1px solid var(--cs-border)", height: 46 }}>
          <Search size={18} style={{ color: "var(--cs-fg2)" }} />
          <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="bg-transparent outline-none flex-1 text-sm" style={{ color: "#fff" }} />
        </div>
        <select data-testid="admin-users-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="rounded-xl px-3 text-sm outline-none" style={{ height: 46, background: "var(--cs-card)", border: "1px solid var(--cs-border)", color: "#fff" }}>
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
      </div>

      {!users ? <div className="mt-6"><Spinner /></div> : (
        <div className="cs-card mt-4 overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: 640 }}>
            <thead>
              <tr style={{ color: "var(--cs-fg2)" }} className="text-left text-xs">
                <th className="p-3 font-semibold">User</th>
                <th className="p-3 font-semibold">Role</th>
                <th className="p-3 font-semibold">Band</th>
                <th className="p-3 font-semibold">Status</th>
                <th className="p-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} style={{ borderTop: "1px solid var(--cs-border)" }}>
                  <td className="p-3">
                    <p className="font-semibold">{u.name}</p>
                    <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{u.email}</p>
                  </td>
                  <td className="p-3">
                    <select data-testid={`admin-user-role-${u.id}`} value={u.role} onChange={(e) => setRole(u, e.target.value)}
                      className="rounded-lg px-2 py-1.5 text-xs outline-none" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)", color: "#fff" }}>
                      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <select data-testid={`admin-user-band-${u.id}`} value={u.band_id || ""} onChange={(e) => setBand(u, e.target.value)}
                      className="rounded-lg px-2 py-1.5 text-xs outline-none" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)", color: "#fff", maxWidth: 150 }}>
                      <option value="">— None —</option>
                      {bands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <span className="text-[11px] rounded-full px-2 py-0.5 font-semibold" style={{ background: u.active ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)", color: u.active ? "var(--cs-green)" : "var(--cs-red)" }}>
                      {u.active ? "Active" : "Suspended"}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button data-testid={`admin-user-toggle-${u.id}`} onClick={() => toggleActive(u)} title={u.active ? "Suspend" : "Reactivate"}
                        className="cs-tap rounded-lg p-2" style={{ background: "var(--cs-card-hi)", border: "1px solid var(--cs-border)" }}>
                        <Power size={15} style={{ color: u.active ? "var(--cs-amber)" : "var(--cs-green)" }} />
                      </button>
                      <button data-testid={`admin-user-delete-${u.id}`} onClick={() => del(u)} title="Delete"
                        className="cs-tap rounded-lg p-2" style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}>
                        <Trash2 size={15} style={{ color: "var(--cs-red)" }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p className="p-6 text-center text-sm" style={{ color: "var(--cs-fg2)" }}>No users match.</p>}
        </div>
      )}
    </Page>
  );
}
